import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addTV, getAllTVs, fetchTVsByStoreId, updateTV, deleteTV, assignMediaToTV } from '../../services/firebaseService';

const initialState = {
  tvs: [],
  selectedTV: null,
  pairingCode: null,
  loading: false,
  error: null,
  syncing: false,
};

// Async thunks
export const fetchTVs = createAsyncThunk(
  'tv/fetchTVs',
  async (userId, { rejectWithValue }) => {
    try {
      return new Promise((resolve, reject) => {
        getAllTVs(userId, (tvs) => {
          resolve(tvs);
        }, reject);
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createTV = createAsyncThunk(
  'tv/createTV',
  async ({ userId, tvData }, { rejectWithValue }) => {
    try {
      const tvId = await addTV(userId, tvData);
      return { id: tvId, ...tvData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const modifyTV = createAsyncThunk(
  'tv/modifyTV',
  async ({ userId, tvId, tvData }, { rejectWithValue }) => {
    try {
      await updateTV(userId, tvId, tvData);
      return { tvId, tvData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const removeTV = createAsyncThunk(
  'tv/removeTV',
  async ({ userId, tvId }, { rejectWithValue }) => {
    try {
      await deleteTV(userId, tvId);
      return tvId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const linkMediaToTV = createAsyncThunk(
  'tv/linkMediaToTV',
  async ({ userId, tvId, mediaUrl, mediaName }, { rejectWithValue }) => {
    try {
      await assignMediaToTV(userId, tvId, mediaUrl, mediaName);
      return { tvId, mediaUrl, mediaName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch TVs from all branches (storeIds) in takeawayList
 * @param {Array} storeIds - Array of store IDs (license_keys) from takeawayList
 * @returns {Promise<Array>} - Combined array of all TVs from all branches
 */
export const fetchTVsFromAllBranches = createAsyncThunk(
  'tv/fetchTVsFromAllBranches',
  async (storeIds, { rejectWithValue }) => {
    try {
      if (!storeIds || storeIds.length === 0) {
        return [];
      }

      // Fetch all TVs filtered by all storeIds at once (more efficient - single Firebase call)
      const allTVs = await fetchTVsByStoreId(storeIds);
      
      return allTVs;
    } catch (error) {
      console.error('Error fetching TVs from all branches:', error);
      return rejectWithValue(error.message);
    }
  }
);

const tvSlice = createSlice({
  name: 'tv',
  initialState,
  reducers: {
    setTVs: (state, action) => {
      state.tvs = action.payload;
    },
    selectTV: (state, action) => {
      state.selectedTV = action.payload;
    },
    setPairingCode: (state, action) => {
      state.pairingCode = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSyncing: (state, action) => {
      state.syncing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch TVs
      .addCase(fetchTVs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTVs.fulfilled, (state, action) => {
        state.loading = false;
        state.tvs = action.payload;
      })
      .addCase(fetchTVs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create TV
      .addCase(createTV.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTV.fulfilled, (state, action) => {
        state.loading = false;
        state.tvs.push(action.payload);
      })
      .addCase(createTV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Modify TV
      .addCase(modifyTV.fulfilled, (state, action) => {
        const { tvId, tvData } = action.payload;
        const index = state.tvs.findIndex((tv) => tv.id === tvId);
        if (index !== -1) {
          state.tvs[index] = { ...state.tvs[index], ...tvData };
        }
      })
      // Remove TV
      .addCase(removeTV.fulfilled, (state, action) => {
        state.tvs = state.tvs.filter((tv) => tv.id !== action.payload);
      })
      // Link Media to TV
      .addCase(linkMediaToTV.fulfilled, (state, action) => {
        const { tvId, mediaUrl, mediaName } = action.payload;
        const tv = state.tvs.find((tv) => tv.id === tvId);
        if (tv) {
          tv.assignedMedia = mediaUrl;
          tv.assignedMediaName = mediaName;
        }
      })
      // Fetch TVs from all branches
      .addCase(fetchTVsFromAllBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTVsFromAllBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.tvs = action.payload;
      })
      .addCase(fetchTVsFromAllBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setTVs, selectTV, setPairingCode, clearError, setSyncing } = tvSlice.actions;

export default tvSlice.reducer;

